import { getActiveFunders, getFunderFromUser } from "@engine/round/accessors";
import { type Funder, type RoundAggregate } from "@engine/round/aggregate";
import { useLocalStorage } from "@sfc/ui";
import { useCallback, useMemo } from "react";

const isNullableString = (value: unknown): value is string | null =>
  value == null || typeof value === "string";

/**
 * Which funder the workspace is showing.
 *
 * **Not the same question as "who is the viewer".** A funder sees their own
 * record; an SFC ADMIN is usually not a funder at all, and gets to pick whose
 * workspace they're looking at (remembered per admin, per round). Anything that
 * reads funder-scoped state — ratings, overall scores, allocations — has to
 * resolve it this way or it reads nothing for an admin and silently shows an
 * empty workspace.
 *
 * That is exactly what happened: the To Do rail called `getFunderFromUser`
 * directly, so for an admin every funder-derived step ("Set a score", Scoring,
 * Evaluations) sat permanently at zero next to a workspace displaying real
 * values. One shared resolver, so a second consumer can't drift from the first.
 */
export function useWorkspaceSelectedFunder({
  round,
  actingAsUserId,
  effectiveIsSfcAdmin,
}: {
  round: RoundAggregate;
  actingAsUserId: string;
  effectiveIsSfcAdmin: boolean;
}) {
  const funders = useMemo(
    () =>
      getActiveFunders(round)
        .slice()
        .sort((left, right) => left.shortname.localeCompare(right.shortname)),
    [round],
  );

  // Same aggregate object identity as the `funders` entry, so downstream memos
  // keyed on the result are unaffected.
  const viewerFunder = getFunderFromUser(round, actingAsUserId);
  const canSelectFunder = effectiveIsSfcAdmin && viewerFunder == null;
  const [adminSelectedFunderId, setAdminSelectedFunderId] = useLocalStorage<
    string | null
  >(`funderWorkspaceSelectedFunderId:${actingAsUserId}:${round.id}`, null, {
    validate: isNullableString,
  });

  const selectedFunder: Funder | null = useMemo(
    () =>
      viewerFunder ??
      (canSelectFunder
        ? (funders.find((funder) => funder.id === adminSelectedFunderId) ??
          funders[0] ??
          null)
        : null),
    [adminSelectedFunderId, canSelectFunder, funders, viewerFunder],
  );

  const selectFunder = useCallback(
    (funderId: string) => {
      if (canSelectFunder && funders.some((funder) => funder.id === funderId)) {
        setAdminSelectedFunderId(funderId);
      }
    },
    [canSelectFunder, funders, setAdminSelectedFunderId],
  );

  return { funders, selectedFunder, canSelectFunder, selectFunder };
}
